import { getSharedAudioGraph } from "./audioRouting";

/**
 * AvatarStreamService.js — Redesigned
 *
 * KEY CHANGES:
 *
 * 1. No polling / setTimeout waiting for the video element.
 *    The video element is registered via a React callback-ref in AIPanel
 *    (webrtcVideoCallbackRef).  The setter fires the _videoElementResolver
 *    Promise immediately, so there is 0-ms overhead.
 *
 * 2. handleInitAvatarStream() is triggered as soon as the WebSocket delivers
 *    the "init_avatar_stream" message — which the backend now sends before
 *    generating any greeting text.  ICE negotiation therefore runs in parallel
 *    with LLM warm-up.
 *
 * 3. The answer-ready event is emitted as soon as setLocalDescription completes
 *    (not after the full connection is established), matching D-ID's expected
 *    flow (SDP answer → ICE candidates arrive → stream starts).
 *
 * 4. attachStreamToVideo() is idempotent and safe to call multiple times.
 *
 * 5. Cleanup resets all state so a page reload / reconnect starts fresh.
 */

class AvatarStreamService {
  constructor() {
    this.mode             = null;   // 'did' | 'static' | null
    this.peerConnection   = null;
    this._videoElement    = null;   // backing field; use getter/setter
    this.listeners        = {};
    this.streamData       = null;
    this.isInitialized    = false;
    this.interviewId      = null;
    this.pendingStream    = null;   // MediaStream received before video element was mounted

    // Resolved the instant the video element is registered (callback-ref in AIPanel)
    this._videoElementResolver = null;
  }

  // ── Video element getter / setter ──────────────────────────────────────────
  get videoElement() { return this._videoElement; }

  set videoElement(el) {
    this._videoElement = el;

    // Resolve the waiting promise immediately
    if (el && this._videoElementResolver) {
      console.log("✅ [AvatarStreamService] Video element registered — resolving wait promise");
      const resolve = this._videoElementResolver;
      this._videoElementResolver = null;
      resolve();
    }

    // If a MediaStream arrived before the element was mounted, attach it now
    if (el && this.pendingStream) {
      console.log("✅ [AvatarStreamService] Attaching pending stream to video element");
      this.attachStreamToVideo(this.pendingStream);
      this.pendingStream = null;
    }
  }

  // ── Event bus ─────────────────────────────────────────────────────────────
  on(event, callback) {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(callback);
  }

  removeListener(event, callback) {
    if (this.listeners[event])
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
  }

  emit(event, data) {
    (this.listeners[event] || []).forEach(cb => {
      try { cb(data); } catch (e) { console.error(`[AvatarStreamService] Error in ${event} listener:`, e); }
    });
  }

  // ── Initialize (called from AIPanel on mount) ─────────────────────────────
  async initialize(interviewId, videoElement = null) {
    this.interviewId   = interviewId;
    this.isInitialized = true;

    if (videoElement) {
      console.log("✅ [AvatarStreamService] Video element provided at initialization");
      this.videoElement = videoElement;
    }

    console.log("🎬 AvatarStreamService initialized — interview:", interviewId);
    this.emit("ready");
  }

  // ── Handle WebRTC offer from backend ─────────────────────────────────────
  /**
   * Called by VoiceInterview when it receives an "init_avatar_stream" WS message.
   *
   * Because the backend now sends this before generating the greeting, this will
   * be called much earlier than before — the WebRTC negotiation will complete
   * while the LLM is still building the first question.
   */
  async handleInitAvatarStream(streamData) {
    console.log("🎬 [AvatarStreamService] WebRTC offer received");
    console.log("   Stream ID:", streamData.stream_id);
    console.log("   ICE servers:", streamData.ice_servers?.length ?? 0);

    this.streamData = streamData;
    this.mode       = "did";

    // Tell AIPanel to mount the WebRTC <video> element (if not already)
    this.emit("mode-changed", { mode: "did", type: "webrtc" });

    // ── Wait for the <video> element (event-driven, no polling) ───────────
    if (!this.videoElement) {
      console.log("⏳ [AvatarStreamService] Waiting for video element…");
      await new Promise(resolve => {
        this._videoElementResolver = resolve;
        // Safety cap at 3 s
        setTimeout(() => {
          if (this._videoElementResolver) {
            console.warn("⚠️ [AvatarStreamService] Video element wait timed out (3 s)");
            this._videoElementResolver = null;
            resolve();
          }
        }, 3000);
      });
    }

    if (this.videoElement) {
      console.log("✅ [AvatarStreamService] Video element ready");
    } else {
      console.warn("⚠️ [AvatarStreamService] No video element — stream will attach when element mounts");
    }

    try {
      await this.setupDIDWebRTC(streamData);
      console.log("✅ [AvatarStreamService] WebRTC setup complete");
    } catch (error) {
      console.error("❌ [AvatarStreamService] WebRTC setup failed:", error);
      this.fallbackToStatic();
    }
  }

  // ── Handle static avatar video message ────────────────────────────────────
  handleAvatarVideo(videoConfig) {
    console.log("🎬 [AvatarStreamService] Static avatar_video received:", videoConfig);
    this.mode       = "static";
    this.streamData = videoConfig;
    this.emit("mode-changed", {
      mode:     "static",
      type:     videoConfig.mode,
      videoUrl: videoConfig.video_url,
      loop:     videoConfig.loop,
      muted:    videoConfig.muted,
    });
  }

  // ── Handle custom avatar frame ─────────────────────────────────────────────
  handleCustomAvatarFrame(frameData, frameIndex) {
    // Only set mode once, not on every frame (would cause constant re-renders)
    if (this.mode !== "custom") {
      this.mode = "custom";
      this.emit("mode-changed", { mode: "custom" });
    }
    this.emit("custom-avatar-frame", { frameData, frameIndex, timestamp: Date.now() });
  }

  // ── Handle custom avatar audio chunk ──────────────────────────────────────
  handleCustomAvatarAudio(audioData) {
    this.emit("custom-avatar-audio", { audioData, timestamp: Date.now() });
  }

  // ── Core WebRTC setup ─────────────────────────────────────────────────────
  async setupDIDWebRTC(streamData) {
    const { ice_servers: iceServers = [], offer } = streamData;

    console.log("🎬 [AvatarStreamService] Setting up RTCPeerConnection");
    console.log("   ICE servers:", iceServers.length);

    const peerConfig = { iceServers: iceServers.length > 0 ? iceServers : undefined };
    this.peerConnection = new RTCPeerConnection(peerConfig);

    // Connection state monitoring
    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection.connectionState;
      console.log(`📡 [WebRTC] Connection state: ${state}`);
      if (state === "failed") {
        console.error("❌ [WebRTC] Connection FAILED");
        this.emit("webrtc-failed", {
          reason:   "Connection failed",
          iceState: this.peerConnection.iceConnectionState,
        });
        this.fallbackToStatic();
      }
    };

    // ICE gathering state
    this.peerConnection.onicegatheringstatechange = () => {
      console.log(`🧊 [WebRTC] ICE gathering: ${this.peerConnection.iceGatheringState}`);
    };

    // ICE candidates → emit so VoiceInterview can forward to backend
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.emit("ice-candidate", { candidate: event.candidate });
      }
    };

    // Track / stream arrival → attach to <video>
    this.peerConnection.ontrack = (event) => {
      console.log("📹 [WebRTC] Track received:", event.track.kind);
      const stream = event.streams?.[0];
      if (!stream) return;

      if (this.videoElement) {
        this.attachStreamToVideo(stream);
      } else {
        console.log("📹 [WebRTC] Buffering stream until video element mounts");
        this.pendingStream = stream;
      }
    };

    // ── Set remote description (D-ID's offer) ─────────────────────────────
    if (!offer) {
      throw new Error("No SDP offer in streamData");
    }

    await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
    console.log("✅ [WebRTC] Remote offer set");

    // ── Create and set local answer ────────────────────────────────────────
    const answer = await this.peerConnection.createAnswer();
    if (!answer?.sdp) throw new Error("Failed to create SDP answer");

    await this.peerConnection.setLocalDescription(answer);
    console.log("✅ [WebRTC] Local answer set");

    // ── Emit answer immediately (don't wait for ICE completion) ───────────
    // D-ID expects the SDP answer quickly; ICE candidates trickle in after.
    this.emit("answer-ready", { answer });

    console.log("🎬 [WebRTC] SDP answer emitted — ICE negotiation will proceed asynchronously");
  }

  // ── Attach MediaStream to <video> element ─────────────────────────────────
  attachStreamToVideo(stream) {
    const el = this.videoElement;
    if (!el) {
      console.warn("⚠️ [AvatarStreamService] attachStreamToVideo: no video element");
      this.pendingStream = stream;
      return;
    }

    if (el.srcObject === stream) return; // already attached

    try {
      el.srcObject = stream;
      el.play().catch(e => {
        console.warn("⚠️ [AvatarStreamService] video.play() blocked:", e.message);
      });
      console.log("✅ [AvatarStreamService] Stream attached to video element");

      // Drive speaking state from ACTUAL audio energy — not WS timestamps.
      // avatar_speaking_start/end arrive before peer-to-peer audio reaches
      // the client, so we analyse the stream itself for real activity.
      this._startAudioActivityDetection(stream);
    } catch (e) {
      console.error("❌ [AvatarStreamService] Failed to attach stream:", e);
    }
  }

  // ── Real-time audio activity detection ────────────────────────────────────
  // Emits "did-audio-playing" when genuine speech energy is detected.
  // Emits "did-audio-silent"  when audio drops back to silence for >900 ms.
  _startAudioActivityDetection(stream) {
    this._stopAudioActivityDetection(); // tear down any previous instance

    try {
      const shared = getSharedAudioGraph();
      if (!shared) {
        console.warn("⚠️ [AvatarStreamService] Shared audio graph not available yet; skipping activity detection");
        return;
      }

      const audioCtx = shared.audioContext;
      const source   = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      // Do NOT connect to destination — analyse only, don't double-play

      const dataArray  = new Uint8Array(analyser.frequencyBinCount);
      const THRESHOLD  = 10;    // RMS level above which we consider audio active
      const ONSET_MS   = 80;    // continuous audio for this long → fire playing
      const SILENCE_MS = 900;   // continuous silence for this long → fire silent

      let isSpeaking   = false;
      let onsetTimer   = null;
      let silenceTimer = null;

      const tick = () => {
        if (!this._activityAnalyser) return; // cleaned up — stop loop
        analyser.getByteFrequencyData(dataArray);

        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i] * dataArray[i];
        const rms = Math.sqrt(sum / dataArray.length);

        if (rms > THRESHOLD) {
          if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
          if (!isSpeaking && !onsetTimer) {
            onsetTimer = setTimeout(() => {
              onsetTimer = null;
              isSpeaking = true;
              console.log("🎤 [ActivityDetect] Audio PLAYING (rms:", rms.toFixed(1), ")");
              this.emit("did-audio-playing");
            }, ONSET_MS);
          }
        } else {
          if (onsetTimer) { clearTimeout(onsetTimer); onsetTimer = null; }
          if (isSpeaking && !silenceTimer) {
            silenceTimer = setTimeout(() => {
              silenceTimer = null;
              isSpeaking   = false;
              console.log("🔇 [ActivityDetect] Audio SILENT");
              this.emit("did-audio-silent");
            }, SILENCE_MS);
          }
        }

        this._activityRafId = requestAnimationFrame(tick);
      };

      this._activityAudioCtx = audioCtx;
      this._activityAnalyser = analyser; // non-null = loop is running
      this._activityRafId    = requestAnimationFrame(tick);

      console.log("✅ [AvatarStreamService] Audio activity detection started");
    } catch (e) {
      console.warn("⚠️ [AvatarStreamService] Could not start activity detection:", e);
    }
  }

  _stopAudioActivityDetection() {
    if (this._activityRafId) {
      cancelAnimationFrame(this._activityRafId);
      this._activityRafId = null;
    }
    this._activityAnalyser = null; // signals tick loop to stop
    if (this._activityAudioCtx) {
      try { this._activityAudioCtx.close(); } catch (e) {}
      this._activityAudioCtx = null;
    }
  }

  // ── Handle ICE candidate from backend ─────────────────────────────────────
  handleICECandidate(candidate) {
    if (this.peerConnection) {
      try {
        this.peerConnection
          .addIceCandidate(new RTCIceCandidate(candidate))
          .catch(e => console.warn("⚠️ [AvatarStreamService] Failed to add ICE candidate:", e));
      } catch (e) {
        console.warn("⚠️ [AvatarStreamService] ICE candidate error:", e);
      }
    }
  }

  // ── Fallback ──────────────────────────────────────────────────────────────
  fallbackToStatic() {
    console.warn("⚠️ [AvatarStreamService] Falling back to static mode");
    this.mode = "static";
    this.emit("mode-changed", { mode: "static", type: "speaking", fallback: true });
  }

  // ── State helpers ─────────────────────────────────────────────────────────
  getMode()     { return this.mode; }
  isDIDMode()   { return this.mode === "did"; }
  isStaticMode(){ return this.mode === "static"; }

  // ── Debug ─────────────────────────────────────────────────────────────────
  getDebugInfo() {
    return {
      mode:                   this.mode,
      isInitialized:          this.isInitialized,
      videoElementPresent:    !!this.videoElement,
      pendingStreamPresent:   !!this.pendingStream,
      peerConnectionPresent:  !!this.peerConnection,
      peerConnectionState:    this.peerConnection?.connectionState ?? null,
      iceConnectionState:     this.peerConnection?.iceConnectionState ?? null,
      listenersRegistered:    Object.fromEntries(
        Object.entries(this.listeners).map(([k, v]) => [k, v.length])
      ),
    };
  }

  // ── Cleanup ────────────────────────────────────────────────────────────────
  cleanup() {
    this._stopAudioActivityDetection();

    if (this.peerConnection) {
      this.peerConnection.ontrack              = null;
      this.peerConnection.onicecandidate       = null;
      this.peerConnection.onconnectionstatechange = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }

    this.mode               = null;
    this.streamData         = null;
    this.pendingStream      = null;
    this._videoElementResolver = null;
    this.isInitialized      = false;

    console.log("🧹 [AvatarStreamService] Cleaned up");
  }
}

// Singleton
const avatarStreamService = new AvatarStreamService();
export default avatarStreamService;
